<template>
  <div class="container-fluid mt-4">
    <div class="row mb-5">
      <div class="col-lg-3">
        <side-nav :sections="managerSideNav" />
      </div>
      <div class="col-lg-9 mt-lg-0 mt-4">
        <!-- 로딩 상태 -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- 데이터 로드 완료 -->
        <template v-else-if="managerData">
          <!-- 기본 정보 카드 -->
          <basic-info-card
            :basic-data="managerData"
            :is-editor="false"
            @update-field="handleUpdateField"
            @save-changes="handleSaveChanges"
          />

          <!-- 비밀번호 변경 카드 -->
          <password-change-card @update-password="handlePasswordChange" />

          <!-- 계정 상태 관리 카드 -->
          <account-status-card
            :current-status="managerData.active_yn"
            :is-employee="true"
            @deactivate-account="handleDeactivateAccount"
            @activate-account="handleActivateAccount"
          />
        </template>

        <!-- 데이터 없음 -->
        <div v-else class="alert alert-warning">
          관리자 정보를 찾을 수 없습니다.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BasicInfoCard from "@/views/users/components/BasicInfoCard.vue";
import PasswordChangeCard from "@/views/users/components/PasswordChangeCard.vue";
import AccountStatusCard from "@/views/users/components/AccountStatusCard.vue";
import SideNav from "@/views/users/components/SideNav.vue";

export default {
  name: "ManagerDetail",
  components: {
    BasicInfoCard,
    PasswordChangeCard,
    AccountStatusCard,
    SideNav,
  },
  data() {
    return {
      managerId: null,
      managerData: null,
      loading: false,
      updateFields: {},
      managerSideNav: [
        { id: 'basic-info', icon: 'receipt_long', label: '기본 정보' },
        { id: 'password', icon: 'lock', label: '비밀번호 변경' },
        { id: 'account-status', icon: 'verified_user', label: '계정 상태' },
      ],
    };
  },
  created() {
    this.managerId = this.$store.state.auth.user_info.idx;
    this.loadManagerData();
  },
  methods: {
    async loadManagerData() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/roles/employees/${this.managerId}`,
          loadingBar: true,
        });
        if (res.status === 200) {
          this.managerData = res.data;
        }
      } catch (error) {
        console.error("관리자 정보 로드 실패:", error);
        if (error.response?.status === 404) {
          alert("관리자를 찾을 수 없습니다.");
        } else {
          alert("관리자 정보를 불러오는데 실패했습니다.");
        }
      } finally {
        this.loading = false;
      }
    },

    handleUpdateField({ field, value }) {
      //console.log('handleUpdateField 호출:', field, value);
      this.updateFields = {
        ...this.updateFields,
        [field]: value
      };
      //console.log('updateFields 업데이트:', this.updateFields);
    },

    async handleSaveChanges() {
      //console.log('저장 시도, updateFields:', this.updateFields);
      if (Object.keys(this.updateFields).length === 0) {
        alert("변경된 내용이 없습니다.");
        return;
      }

      try {
        //console.log('요청 URL:', `/roles/employees/${this.managerId}`);
        //console.log('요청 데이터:', JSON.stringify(this.updateFields, null, 2));

        await this.$axios({
          method: "put",
          url: `/roles/employees/${this.managerId}`,
          data: this.updateFields
        });

        //console.log('응답:', res);
        alert("관리자 정보가 성공적으로 수정되었습니다.");
        this.updateFields = {};
        await this.loadManagerData();
      } catch (error) {
        //console.error("관리자 정보 수정 실패 - 전체 에러:", error);
        //console.error("응답 데이터:", error.response?.data);
        //console.error("응답 상태:", error.response?.status);
        //console.error("요청 설정:", error.config);

        let errorMessage = "관리자 정보 수정에 실패했습니다.";

        if (error.response?.status === 404) {
          errorMessage = "관리자를 찾을 수 없습니다.";
        } else if (error.response?.data) {
          // 백엔드에서 반환하는 다양한 에러 형식 처리
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }

        alert(`${errorMessage}\n\n상세 정보는 콘솔을 확인하세요.`);
      }
    },

    async handlePasswordChange({ current, new: newPassword }) {
      try {
        await this.$axios({
          method: "post",
          url: "/auth/emp/change-password",
          data: {
            current_password: current,
            new_password: newPassword
          },
          loadingBar: true
        });
        alert("비밀번호가 변경되었습니다.");
      } catch (error) {
        console.error("비밀번호 변경 실패:", error);
        const msg = error?.response?.data?.detail || error?.response?.data?.message || "비밀번호 변경에 실패했습니다.";
        alert(msg);
      }
    },

    async handleDeactivateAccount() {
      if (!confirm("정말 이 계정을 비활성화하시겠습니까?")) {
        return;
      }

      try {
        await this.$axios({
          method: "patch",
          url: `/roles/employees/${this.managerId}/status`,
          data: {
            status: "N"
          },
          loadingBar: true
        });

        alert("계정이 비활성화되었습니다.");
        await this.loadManagerData();
      } catch (error) {
        console.error("계정 비활성화 실패:", error);
        alert(error.response?.data?.detail || "계정 비활성화에 실패했습니다.");
      }
    },

    async handleActivateAccount() {
      if (!confirm("정말 이 계정을 활성화하시겠습니까?")) {
        return;
      }

      try {
        await this.$axios({
          method: "patch",
          url: `/roles/employees/${this.managerId}/status`,
          data: {
            status: "Y"
          },
          loadingBar: true
        });

        alert("계정이 활성화되었습니다.");
        await this.loadManagerData();
      } catch (error) {
        console.error("계정 활성화 실패:", error);
        alert(error.response?.data?.detail || "계정 활성화에 실패했습니다.");
      }
    },
  },
};
</script>
